import { useEffect, useRef, useState } from "react";

/**
 * Scroll-triggered reveal animation. Fades + translates children into view
 * once they intersect the viewport. Respects prefers-reduced-motion.
 *
 * Use this across pages for consistent app-like section transitions.
 */
export function Reveal({
  children,
  as: Tag = "div" as any,
  delay = 0,
  y = 24,
  className = "",
  ...rest
}: {
  children: React.ReactNode;
  as?: any;
  delay?: number;
  y?: number;
  className?: string;
  [key: string]: any;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref as any}
      {...rest}
      className={className}
      style={{
        ...(rest.style ?? {}),
        opacity: shown ? 1 : 0,
        transform: shown ? "translate3d(0,0,0)" : `translate3d(0, ${y}px, 0)`,
        transition: `opacity 700ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 800ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </Tag>
  );
}


